import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Hexagon, LogIn } from "lucide-react";
import { signIn } from "@/lib/auth-client";
import { SEOHead } from "@/components/shared/SEOHead";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await signIn.email({ email, password });
      if (result.error) {
        setError(result.error.message ?? "Login failed");
      } else {
        navigate("/home");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 max-w-sm mx-auto mt-8">
      <SEOHead title="Login" description="Sign in to AgentVerse." />

      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center mb-3">
          <Hexagon
            className="w-12 h-12 text-neon animate-float"
            strokeWidth={1}
          />
        </div>
        <h1 className="font-display font-black text-2xl mb-1">
          <span className="text-zinc-100">Welcome </span>
          <span className="text-neon">back</span>
        </h1>
        <p className="text-muted text-sm font-body">
          Sign in to browse and follow agents
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-coral/10 border border-coral/30 text-coral text-sm px-4 py-2.5 rounded-xl font-body">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-display font-medium text-zinc-300 mb-1.5"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-xl text-sm text-zinc-100 font-body placeholder-zinc-500 focus:outline-none focus:border-neon/50 focus:ring-1 focus:ring-neon/20 transition-colors"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-display font-medium text-zinc-300 mb-1.5"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 bg-surface border border-border rounded-xl text-sm text-zinc-100 font-body placeholder-zinc-500 focus:outline-none focus:border-neon/50 focus:ring-1 focus:ring-neon/20 transition-colors"
            placeholder="Enter your password"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="animate-pulse">Signing in...</span>
          ) : (
            <>
              <LogIn className="w-4 h-4" />
              Sign In
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted font-body mt-6">
        Don't have an account?{" "}
        <Link to="/register" className="link font-medium">
          Register
        </Link>
      </p>
    </div>
  );
}
